const defaultWidth = 256;
const defaultHeight = 256;
const defaultMaxZoom = 21;
const defaultMinZoom = 0;

const defaultWmsProperties = {
  REQUEST: 'GetMap',
  SERVICE: 'WMS',
  VERSION: '1.1.1',
  BGCOLOR: '0xFFFFFF',
  TRANSPARENT: 'TRUE',
  SRS: 'EPSG:900913',
  WIDTH: defaultWidth.toString(),
  HEIGHT: defaultHeight.toString(),
  FORMAT: 'image/png',
};

export default function getMapType(
  geoserverHost: string,
  layers: string[],
  styles: string[],
  onAllTilesLoaded?: () => void
) {
  const pendingTiles = new Set<string>();
  let tilesLoadedCallback = onAllTilesLoaded;
  let checkTimeout: number | null = null;

  const checkAllTilesLoaded = () => {
    // Clear any existing timeout
    if (checkTimeout) {
      clearTimeout(checkTimeout);
    }

    // Wait 500ms to ensure all tiles in the current batch have been requested
    checkTimeout = window.setTimeout(() => {
      if (pendingTiles.size === 0 && tilesLoadedCallback) {
        tilesLoadedCallback();
        tilesLoadedCallback = undefined; // Only fire once
      }
    }, 500);
  };

  const WmsMapType = {
    name: geoserverHost,
    getTileUrl: function (coord: { x: number; y: number }, zoom: number) {
      const wmsParams = {
        ...defaultWmsProperties,
        LAYERS: layers.join(','),
        STYLES: styles.join(','),
      };
      const lULP = new google.maps.Point(coord.x * 256, (coord.y + 1) * 256);
      const lLRP = new google.maps.Point((coord.x + 1) * 256, coord.y * 256);

      const projectionMap = new MercatorProjection();

      const lULg = projectionMap.fromDivPixelToSphericalMercator(lULP, zoom);
      const lLRg = projectionMap.fromDivPixelToSphericalMercator(lLRP, zoom);

      const lUL_Latitude = lULg.y;
      const lUL_Longitude = lULg.x;
      const lLR_Latitude = lLRg.y;
      let lLR_Longitude = lLRg.x;
      //there is a bug when crossing the -180 longitude border (tile does not render) - this check seems to fix it
      if (lLR_Longitude < lUL_Longitude) {
        lLR_Longitude = Math.abs(lLR_Longitude);
      }
      const baseURL = geoserverHost;
      const now = new Date();
      const ts = now.getTime();
      const urlResult =
        baseURL +
        new URLSearchParams(wmsParams as Record<string, string>).toString() +
        '&bbox=' +
        lUL_Longitude +
        ',' +
        lUL_Latitude +
        ',' +
        lLR_Longitude +
        ',' +
        lLR_Latitude +
        '&ts=' +
        ts;

      // Track this tile
      const tileKey = `${coord.x},${coord.y},${zoom}`;
      pendingTiles.add(tileKey);

      // Create an image to preload and track loading
      const img = new Image();
      img.onload = () => {
        pendingTiles.delete(tileKey);
        checkAllTilesLoaded();
      };
      img.onerror = () => {
        pendingTiles.delete(tileKey);
        checkAllTilesLoaded();
      };
      img.src = urlResult;

      return urlResult;
    },
    tileSize: new google.maps.Size(256, 256),
    isPng: true,
    maxZoom: defaultMaxZoom,
    minZoom: defaultMinZoom,
  };
  return WmsMapType;
}

function bound(value: number, opt_min: number | null, opt_max: number | null) {
  if (opt_min != null) value = Math.max(value, opt_min);
  if (opt_max != null) value = Math.min(value, opt_max);
  return value;
}

function degreesToRadians(deg: number) {
  return deg * (Math.PI / 180);
}

function radiansToDegrees(rad: number) {
  return rad / (Math.PI / 180);
}

class MercatorProjection {
  pixelOrigin_: google.maps.Point;
  pixelsPerLonDegree_: number;
  pixelsPerLonRadian_: number;
  constructor() {
    const MERCATOR_RANGE = 256;
    this.pixelOrigin_ = new window.google.maps.Point(
      MERCATOR_RANGE / 2,
      MERCATOR_RANGE / 2
    );
    this.pixelsPerLonDegree_ = MERCATOR_RANGE / 360;
    this.pixelsPerLonRadian_ = MERCATOR_RANGE / (2 * Math.PI);
  }
  fromLatLngToPoint(
    latLng: { lng: () => number; lat: () => number },
    opt_point: google.maps.Point
  ) {
    const point = opt_point || new window.google.maps.Point(0, 0);

    const origin = this.pixelOrigin_;
    point.x = origin.x + latLng.lng() * this.pixelsPerLonDegree_;
    // NOTE(appleton): Truncating to 0.9999 effectively limits latitude to
    // 89.189.  This is about a third of a tile past the edge of the world tile.
    const siny = bound(
      Math.sin(degreesToRadians(latLng.lat())),
      -0.9999,
      0.9999
    );
    point.y =
      origin.y +
      0.5 * Math.log((1 + siny) / (1 - siny)) * -this.pixelsPerLonRadian_;
    return point;
  }
  fromDivPixelToLatLng(pixel: { x: number; y: number }, zoom: number) {
    const origin = this.pixelOrigin_;
    const scale = Math.pow(2, zoom);
    const lng = (pixel.x / scale - origin.x) / this.pixelsPerLonDegree_;
    const latRadians = (pixel.y / scale - origin.y) / -this.pixelsPerLonRadian_;
    const lat = radiansToDegrees(
      2 * Math.atan(Math.exp(latRadians)) - Math.PI / 2
    );
    return new window.google.maps.LatLng(lat, lng);
  }
  fromDivPixelToSphericalMercator(pixel: google.maps.Point, zoom: number) {
    const coord = this.fromDivPixelToLatLng(pixel, zoom);

    const r = 6378137.0;
    const x = r * degreesToRadians(coord.lng());
    const latRad = degreesToRadians(coord.lat());
    const y =
      (r / 2) * Math.log((1 + Math.sin(latRad)) / (1 - Math.sin(latRad)));

    return new window.google.maps.Point(x, y);
  }
}
